import React, { useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router';

import Text from 'src/components/Text';
import DataTable from './sub-component/Table';
import CustomStepper from 'src/components/Stepper';
import { STEP_LABELS, MILESTONE_TO_STEP_INDEX } from 'src/util/enum';

import { useSummaryDetail } from './hooks';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import {
    CardContent,
    Box,
    Container,
    Grid,
    Typography,
    useTheme,
    IconButton,
    useMediaQuery,
    CircularProgress,
    Divider,
    Stepper,
    Step,
    StepLabel,
} from '@mui/material';
import { useSummary } from '../hooks';
import { getActivityStyle } from 'src/util/activity-color';
import { useSelector } from 'react-redux';
import { RootState } from 'src/store';
import { formatDateTime } from 'src/util/date-format';

interface SummaryDetailProps {
    isLoading: boolean;
}

function SummaryDetailMobile({ isLoading }: SummaryDetailProps) {
    const theme = useTheme();
    const navigate = useNavigate();
    const isMobile = useMediaQuery('(max-width:600px)');
    const
        {
            method:
            {
                setOpen,
                setDataItem,
                setDeleteOpen,
            }
        } = useSummaryDetail();

    const {
        data: { homeDetail, homeDetailGrid, homeDetailLoading, trackingUnitDetailsActive },
        method: { fetchHomeDetail }
    } = useSummary();

    const handleDeleteOpen = (item) => {
        setDataItem(item);
        setDeleteOpen(true);
    };

    const handleBack = () => {
        navigate(-1);
    };

    const dataHome = useSelector((state: RootState) => state.Home.data.dataHome);

    useEffect(() => {
        fetchHomeDetail(dataHome?.booking_no);
    }, [dataHome]);

    // Check visibility permission for Tracking Unit Details
    const isTrackingUnitDetailsAllowed = useMemo(() => {
        if (trackingUnitDetailsActive === false) return false;
        try {
            const cached = localStorage.getItem('CKB_TRACKING_UNIT_DETAILS_ACTIVE');
            if (cached !== null && JSON.parse(cached) === false) {
                return false;
            }
        } catch (e) {
            // ignore
        }
        return true;
    }, [trackingUnitDetailsActive]);

    // Calculate milestone stepper steps and completion status from homeDetailGrid
    const stepsWithStatus = useMemo(() => {
        const defaultSteps = STEP_LABELS.map(label => ({ label, status: 'progress' }));

        const items = homeDetailGrid || [];
        if (!items || items.length === 0) {
            return defaultSteps;
        }

        const subService = dataHome?.sub_service_type || homeDetail?.sub_service_type || items[0]?.sub_service_type || '';
        // Steps to exclude for Road transport mode
        const roadExcludedSteps = ['Port Gate In', 'CY Check In', 'ATD POL'];

        // Filter steps based on transport mode
        let filteredSteps = defaultSteps;
        if (subService?.toLowerCase().includes('land')) {
            filteredSteps = defaultSteps.filter(step => !roadExcludedSteps.includes(step.label));
        }

        let latestMilestoneStepIndex = -1;
        items.forEach((detail: any) => {
            const milestone = detail.milestone;
            if (milestone && MILESTONE_TO_STEP_INDEX.hasOwnProperty(milestone)) {
                const stepIndex = MILESTONE_TO_STEP_INDEX[milestone];
                let adjustedStepIndex = stepIndex;
                if (subService?.toLowerCase().includes('land')) {
                    roadExcludedSteps.forEach(excludedStep => {
                        const excludedStepIndex = STEP_LABELS.indexOf(excludedStep);
                        if (excludedStepIndex !== -1 && excludedStepIndex < stepIndex) {
                            adjustedStepIndex--;
                        }
                    });
                }

                if (adjustedStepIndex > latestMilestoneStepIndex) {
                    latestMilestoneStepIndex = adjustedStepIndex;
                }
            }
        });

        if (latestMilestoneStepIndex === -1) {
            return filteredSteps;
        }

        return filteredSteps.map((step, index) => {
            let status = 'progress';
            if (index <= latestMilestoneStepIndex) {
                status = 'completed';
            } else if (index === latestMilestoneStepIndex) {
                status = 'progress';
            }
            return { ...step, status };
        });
    }, [homeDetailGrid, dataHome, homeDetail]);

    const shipmentNumber = [
        { label: 'Shipment Number:', value: dataHome?.sn_number || '-' },
        { label: 'Booking No:', value: dataHome?.booking_no || '-' },
        { label: 'Reference No:', value: dataHome?.reference_no || '-' },
        { label: 'ETD - ETA Vessel:', value: dataHome?.etd_eta || '-' },
        { label: 'POL:', value: dataHome?.pol || '-' },
        { label: 'POD:', value: dataHome?.pod || '-' },
        { label: 'Last Activities Date:', value: dataHome?.last_activities_time ? formatDateTime(dataHome?.last_activities_time) : '-' },
    ];

    const bookingInformation = [
        { label: 'Consignee Name:', value: dataHome?.consignee_name || '-' },
        { label: 'Service Type:', value: homeDetail?.service_type || '-' },
        { label: 'Created By:', value: homeDetail?.created_by || '-' },
        { label: 'Created Date:', value: homeDetail?.created_date ? formatDateTime(homeDetail?.created_date) : '-' },
        { label: 'Sub Service Type:', value: homeDetail?.sub_service_type || '-' },
        { label: 'Vehicle Type:', value: homeDetail?.vehicle_type || '-' },
    ];

    const senderDetails = [
        { label: 'Sender Ref. No:', value: homeDetail?.sender_ref_no || '-' },
        { label: 'Name:', value: homeDetail?.sender_name || '-' },
        { label: 'Company:', value: homeDetail?.sender_company || '-' },
        { label: 'Phone:', value: homeDetail?.sender_phone || '-' },
        { label: 'Address:', value: homeDetail?.sender_address || '-' },
        { label: 'City:', value: homeDetail?.sender_city || '-' },
        { label: 'Postal Code:', value: homeDetail?.sender_postal_code || '-' },
        { label: 'State:', value: homeDetail?.sender_state || '-' },
        { label: 'Country:', value: homeDetail?.sender_country || '-' },
        { label: 'Pick Up Date:', value: homeDetail?.pick_up_date ? formatDateTime(homeDetail?.pick_up_date) : '-' },
    ];

    const receiverDetails = [
        { label: 'Receiver Ref. No:', value: homeDetail?.receiver_ref_no || '-' },
        { label: 'Name:', value: homeDetail?.receiver_name || '-' },
        { label: 'Company:', value: homeDetail?.receiver_company || '-' },
        { label: 'Phone:', value: homeDetail?.receiver_phone || '-' },
        { label: 'Address:', value: homeDetail?.receiver_address || '-' },
        { label: 'City:', value: homeDetail?.receiver_city || '-' },
        { label: 'Postal Code:', value: homeDetail?.receiver_postal_code || '-' },
        { label: 'State:', value: homeDetail?.receiver_state || '-' },
        { label: 'Country:', value: homeDetail?.receiver_country || '-' },
        { label: 'Delivery Date:', value: homeDetail?.delivery_date ? formatDateTime(homeDetail?.delivery_date) : '-' },
    ];

    const shipmentInformation = [
        { label: 'Commodity:', value: homeDetail?.commodity || '-' },
        { label: 'Volume:', value: homeDetail?.volume || '-' },
        { label: 'Weight:', value: homeDetail?.weight || '-' },
        { label: 'Instruction Notes:', value: homeDetail?.instruction_notes || '-' },
        { label: 'BL Number:', value: homeDetail?.bl_number || '-' },
        { label: 'Serial Number:', value: '-' },
    ];

    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    const renderDetails = useMemo(() => (details) => (
        <Grid container spacing={0}>
            {!isLoading && details.map((detail, index) => (
                <Grid item xs={6} sm={4} md={12} textAlign={{ sm: 'left' }} key={index}>
                    <React.Fragment >
                        <Box pr={3} pb={2}>
                            <Box sx={{ fontWeight: 'bold', fontSize: '12px' }}>
                                {detail.label}
                            </Box>
                            <Text>
                                <b style={{ color: theme.colors.ckb.primary, fontSize: '12px' }}>{detail.value}</b>
                            </Text>
                        </Box>
                    </React.Fragment>
                </Grid>
            ))}
        </Grid>
    ), [isLoading, theme.colors.ckb.primary, homeDetail]);

    return (
        <>
            <Container maxWidth="xl">
                <Grid container md={12} xs={12}>
                    {
                        isMobile && (
                            <Box width="100%" display="flex" alignItems="center" mb={2} sx={{ borderBottom: '1px solid #000', pb: 1 }}>
                                <IconButton onClick={handleBack} sx={{ color: theme.colors.ckb.primary }}>
                                    <ArrowBackIcon />
                                </IconButton>
                                <Typography variant="h3" sx={{ ml: 1, color: theme.colors.ckb.primary }}>
                                    Detail
                                </Typography>
                            </Box>
                        )
                    }

                    <Grid item xs={12} md={3}>
                        <Box
                            px={3}
                            display="flex"
                            alignItems="center"
                            justifyContent="space-between"
                        >
                            <Box>
                                <Grid container spacing={1}>
                                    <Grid item xs={7}>
                                        <Typography variant="h5" sx={{ color: theme.colors.ckb.primary }}>
                                            {dataHome?.consignee_name}
                                        </Typography>
                                    </Grid>
                                    <Grid item xs={5}>
                                        <Typography pb={2} variant="h6" gutterBottom>
                                            <div style={getActivityStyle(dataHome?.milestone)}>
                                                {dataHome?.milestone}
                                            </div>
                                        </Typography>
                                    </Grid>
                                </Grid>
                            </Box>
                        </Box>
                        <CardContent sx={{ p: 3, pt: 0 }}>
                            <Typography variant="subtitle2">
                                {renderDetails(shipmentNumber)}
                            </Typography>
                        </CardContent>
                    </Grid>

                    {/* Tracking Unit Details: Placed directly above Booking Information */}
                    {isTrackingUnitDetailsAllowed && (
                        <Grid item xs={12} md={12} sx={{ px: 3, pt: 1, pb: 2 }}>
                            <Box mb={2}>
                                <Typography variant="h4" sx={{ color: theme.colors.ckb.primary }}>
                                    Tracking Unit Details
                                </Typography>
                            </Box>
                            {isLoading || homeDetailLoading ? (
                                <Box display="flex" justifyContent="center" alignItems="center" p={3}>
                                    <CircularProgress size={28} />
                                </Box>
                            ) : homeDetailGrid && homeDetailGrid.length > 0 ? (
                                <Box sx={{ width: '100%', overflowX: 'auto', py: 1 }}>
                                    <Stepper alternativeLabel>
                                        {stepsWithStatus.map((step, index) => (
                                            <Step key={index} active={index === stepsWithStatus.length - 1} completed={step.status === 'completed'}>
                                                <StepLabel StepIconComponent={(props) => <CustomStepper {...props} status={step.status} activity={step.label} />}>
                                                    {step.label}
                                                </StepLabel>
                                            </Step>
                                        ))}
                                    </Stepper>
                                </Box>
                            ) : (
                                <Typography variant="body2" color="textSecondary" align="center" sx={{ py: 1.5 }}>
                                    No tracking details available for this booking.
                                </Typography>
                            )}
                            <Divider sx={{ my: 2 }} />
                        </Grid>
                    )}

                    <Grid item xs={12} md={3}>
                        <Box
                            px={3}
                            display="flex"
                            alignItems="center"
                            justifyContent="space-between"
                        >
                            <Box>
                                <Typography variant="h4" sx={{ color: theme.colors.ckb.primary }}>
                                    Booking Information
                                </Typography>
                            </Box>
                        </Box>
                        <CardContent sx={{ p: 3 }}>
                            <Typography variant="subtitle2">
                                {renderDetails(bookingInformation)}
                            </Typography>
                        </CardContent>
                    </Grid>

                    <Grid item xs={12} md={3}>
                        <Box
                            px={3}
                            display="flex"
                            alignItems="center"
                            justifyContent="space-between"
                        >
                            <Box>
                                <Typography variant="h4" sx={{ color: theme.colors.ckb.primary }}>
                                    Shipment Information
                                </Typography>
                            </Box>
                        </Box>
                        <CardContent sx={{ p: 3 }}>
                            <Typography variant="subtitle2">
                                {renderDetails(shipmentInformation)}
                            </Typography>
                        </CardContent>
                    </Grid>

                    {/* Sender/Shipper Detail */}
                    <Grid item xs={12} md={3}>
                        <Box
                            px={3}
                            display="flex"
                            alignItems="center"
                            justifyContent="space-between"
                        >
                            <Box>
                                <Typography variant="h4" sx={{ color: theme.colors.ckb.primary }}>
                                    Sender/Consignee Detail
                                </Typography>
                            </Box>
                        </Box>
                        <CardContent sx={{ p: 3 }}>
                            <Typography variant="subtitle2">
                                {renderDetails(senderDetails)}
                            </Typography>
                        </CardContent>
                    </Grid>

                    <Grid item xs={12} md={3}>
                        <Box
                            px={3}
                            display="flex"
                            alignItems="center"
                            justifyContent="space-between"
                        >
                            <Box>
                                <Typography variant="h4" sx={{ color: theme.colors.ckb.primary }}>
                                    Receiver/Consignee Detail
                                </Typography>
                            </Box>
                        </Box>
                        <CardContent sx={{ p: 3 }}>
                            <Typography variant="subtitle2">
                                {renderDetails(receiverDetails)}
                            </Typography>
                        </CardContent>
                    </Grid>
                </Grid>

                <Grid sx={{ m: 1, pb: 2, minHeight: '300px' }}>
                    <DataTable
                        setOpen={setOpen}
                        dataGrid={homeDetailGrid}
                        setDataItem={setDataItem}
                        handleDeleteOpen={handleDeleteOpen}
                    />
                </Grid>
            </Container>
        </>
    );
}

export default SummaryDetailMobile;
